import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/tabs';
import { Avatar, Loader } from '../../assets/custom-icons/index';
import { SITELINK } from '../../app-routes/Links';
import { LuCamera, LuUsers, LuCrown, LuMessageCircle } from 'react-icons/lu';
import { FaWhatsapp, FaTelegram } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCommunityDetails } from '../../api-services/community/use-community-details';
import { useCommunityMember } from '../../api-services/community/use-community-members';
import { Member } from '../../app-schemas/community/community.interface';
import PostCard from '../home/home-components/PostCard';
import { FeedsQueryKeys } from '../../api-services/feeds/feeds.keys';
import { fetchFeeds } from '../../api-services/feeds/useFetchNewFeeds';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { useEffect, useMemo, useState } from 'react';
import { PostInterface } from '../../app-schemas/posts/post.schema';
import { getFirstLetters, getUser, tinyAlert } from '../../app-utils';
import { UserInterface } from '../../app-schemas/profile.schema';
import apiClient from '../../api-services/ApiClient';
import { useTheme } from '../../context/theme-context';
import { SkeletonFeed } from '../../components/skeletons';

// Skeleton Components
const CommunityDetailsSkeleton = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <main className={`min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
      <div className="py-5 pt-1 rounded-md bg-white dark:bg-neutral-800 flex flex-col gap-2 lg:gap-3 lg:w-[90%] mx-auto box-border">
        <div className="rounded-md bg-white dark:bg-neutral-800 flex flex-col gap-2 lg:gap-3 lg:w-[80%] mx-auto box-border">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mx-3 animate-pulse" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mx-3 animate-pulse" />
          <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded w-full mx-3 animate-pulse" />
          <div className="flex gap-2 flex-wrap px-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            ))}
          </div>
          <div className="w-full mt-4">
            <div className="grid w-full grid-cols-2 gap-2 p-1">
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
            <div className="mt-4">
              <SkeletonFeed count={3} variant="post" />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

// Error Component
const CommunityDetailsError = ({ onRetry, error }: { onRetry: () => void; error: any }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <main className={`min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
          isDarkMode ? 'bg-red-900/30' : 'bg-red-100'
        }`}>
          <svg className={`w-10 h-10 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className={`text-lg font-semibold mb-2 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Failed to load community
        </h3>
        <p className={`text-sm mb-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {error?.message || 'Unable to load community details. Please check your connection.'}
        </p>
        <button
          onClick={onRetry}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </main>
  );
};

// Empty State Component
const CommunityEmptyState = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col items-center justify-center py-20 ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
      <svg className={`w-20 h-20 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No activities yet
      </p>
      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Be the first to post in this community
      </p>
    </div>
  );
};

const CommunityDetails = () => {
  document.body.classList.remove('overflow-hidden');
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const user: UserInterface = getUser();

  const { ref, inView } = useInView();
  const userCommunityDetails = useCommunityDetails();
  const members = useCommunityMember();

  const [isLoadingBtn, setIsLoadingBtn] = useState(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: isFeedLoading } = useInfiniteQuery({
    queryKey: FeedsQueryKeys.all,
    queryFn: fetchFeeds,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined,
  });

  const content = useMemo(
    () =>
      data?.pages.flatMap((page) =>
        page.data.map((post: PostInterface, index: number) => (
          <PostCard
            key={post.post_id}
            index={post.post_id}
            post={post}
            innerRef={page.data.length - 2 === index + 1 ? ref : undefined}
          />
        )),
      ),
    [data, ref],
  );

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage, fetchNextPage]);

  const chatOnWhatsapp = (number: number | null) => {
    const url = `https://wa.me/${number}`;
    window.open(url, '_blank');
  };

  const handleTelegramClick = (number: number | null) => {
    const url = `tg://openchat?phone=${number}`;
    window.open(url, '_blank');
  };

  const leaveCommunity = async () => {
    try {
      setIsLoadingBtn(true);
      await apiClient.delete(`community/leave/${id}`);
      setIsLoadingBtn(false);
      tinyAlert('success', 'Left community successfully!');
      navigate(SITELINK.COMMUNITIES);
    } catch (e) {
      console.log(e);
      tinyAlert('error', 'Failed to leave community');
      setIsLoadingBtn(false);
    }
  };

  const renderProfilePicture = () =>
    user.profile_picture_url ? (
      <img
        alt="profile"
        src={user.profile_picture_url}
        className="object-cover rounded-full w-[40px] h-[40px] bg-gray-200 shadow-sm"
      />
    ) : (
      <div className="flex items-center justify-center w-[40px] h-[40px] bg-[#223f64] rounded-full border border-green-100">
        <h3 className="text-white text-[1.4rem] uppercase font-[600]">
          {getFirstLetters(user.first_name ?? user.username)}
        </h3>
      </div>
    );

  // Show loading skeleton
  if (userCommunityDetails.isLoading || members.isLoading) {
    return <CommunityDetailsSkeleton />;
  }

  // Show error state
  if (userCommunityDetails.isError) {
    return (
      <CommunityDetailsError 
        onRetry={() => userCommunityDetails.refetch()} 
        error={userCommunityDetails.error} 
      />
    );
  }

  return (
    <main className={`min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
      <section className={`${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
        <div className="min-h-screen py-5 pt-1 rounded-md flex flex-col gap-2 lg:gap-3 lg:w-[90%] mx-auto box-border">
          {userCommunityDetails.isSuccess && (
            <>
              <div className={`rounded-md flex flex-col gap-2 lg:gap-3 lg:w-[80%] mx-auto box-border ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
                <h2 className={`text-xl font-bold px-3 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  {userCommunityDetails.data.data.name}
                </h2>
                
                <p className={`lg:w-[480px] px-3 font-medium text-sm ${isDarkMode ? 'text-gray-300' : 'text-black'}`}>
                  created: {userCommunityDetails.data.data.created}
                </p>

                <p className={`lg:w-[480px] px-3 font-medium text-sm ${isDarkMode ? 'text-gray-300' : 'text-black'}`}>
                  {userCommunityDetails.data.data.description
                    ? userCommunityDetails.data.data.description
                    : 'N/A'}
                </p>

                <div className="flex gap-5 flex-wrap items-center px-3">
                  {userCommunityDetails.data.data?.community_tags?.map(
                    (tag: any, index: number) => (
                      <button
                        key={index}
                        className={`flex h-10 items-center px-4 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-100 aria-disabled:opacity-50 cursor-pointer w-[96px] justify-center border rounded-sm font-bold ${
                          isDarkMode 
                            ? 'border-orange-800 bg-neutral-800 text-orange-400' 
                            : 'border-orange-100 bg-white text-orange-100'
                        }`}
                      >
                        {tag}
                      </button>
                    ),
                  )}
                </div>

                <Tabs defaultValue="activity" className="w-full">
                  <TabsList className={`grid w-full grid-cols-2 ${isDarkMode ? 'bg-neutral-700' : 'bg-gray-100'}`}>
                    <TabsTrigger 
                      value="activity"
                      className={`${isDarkMode ? 'data-[state=active]:bg-neutral-800 data-[state=active]:text-white' : ''}`}
                    >
                      Activities
                    </TabsTrigger>

                    <TabsTrigger 
                      value="member"
                      className={`${isDarkMode ? 'data-[state=active]:bg-neutral-800 data-[state=active]:text-white' : ''}`}
                    >
                      <span className={`px-2 rounded-full ${isDarkMode ? 'bg-neutral-600 text-blue-400' : 'bg-gray-100 text-blue-600'}`}>
                        {members?.data?.data?.community_members?.length}
                      </span>
                      &nbsp;&nbsp; Members
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="about">
                    <Card className={isDarkMode ? 'bg-neutral-800 border-gray-700' : ''}>
                      <div className="flex flex-col lg:flex-row">
                        <div>
                          <CardHeader>
                            <CardTitle className="text-[#0A32AA] dark:text-blue-400">Description</CardTitle>
                            <CardDescription className={`lg:w-[480px] ${isDarkMode ? 'text-gray-300' : ''}`}>
                              {userCommunityDetails.data.data.description
                                ? userCommunityDetails.data.data.description
                                : 'N/A'}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <CardTitle className="text-[#0A32AA] dark:text-blue-400">Community Tag</CardTitle>
                            <div className="flex gap-5 flex-wrap items-center">
                              {userCommunityDetails.data.data?.community_tags?.map(
                                (tag: any, index: number) => (
                                  <button
                                    key={index}
                                    className={`flex h-10 items-center px-4 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-100 aria-disabled:opacity-50 cursor-pointer w-[96px] justify-center border rounded-sm font-bold ${
                                      isDarkMode 
                                        ? 'border-orange-800 bg-neutral-800 text-orange-400' 
                                        : 'border-orange-100 bg-white text-orange-100'
                                    }`}
                                  >
                                    {tag}
                                  </button>
                                ),
                              )}
                            </div>
                          </CardContent>
                        </div>

                        <div>
                          <CardHeader className="flex flex-col gap-2">
                            <CardTitle className="w-full text-[#0A32AA] dark:text-blue-400">
                              Community Information
                            </CardTitle>
                            <div className="flex flex-col gap-10">
                              <CardDescription className={`w-full flex justify-between items-center ${isDarkMode ? 'text-gray-300' : ''}`}>
                                <div className="flex gap-1 items-center">
                                  <img src={Avatar} className="w-[32px] h-[32px]" alt="" />
                                  <p className="text-xs">
                                    {userCommunityDetails.data?.data?.creator?.username ??
                                      userCommunityDetails.data?.data?.creator?.first_name}
                                  </p>
                                </div>
                                <p>Creator</p>
                              </CardDescription>
                              <CardDescription className={`w-full flex justify-between items-center ${isDarkMode ? 'text-gray-300' : ''}`}>
                                <p>Members</p>
                                <p>{userCommunityDetails.data.data.members}</p>
                              </CardDescription>
                              <CardDescription className={`w-full flex justify-between items-center ${isDarkMode ? 'text-gray-300' : ''}`}>
                                <p>Created</p>
                                <p>{userCommunityDetails.data.data.created}</p>
                              </CardDescription>
                            </div>
                          </CardHeader>
                        </div>
                      </div>
                    </Card>
                  </TabsContent>

                  <TabsContent value="activity">
                    <section className="mb-20">
                      <div className={`relative border-t px-2 py-2 flex gap-1 item-center justify-center w-full ${
                        isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white border-gray-300'
                      }`}>
                        <Link to={SITELINK.MY_PROFILE} className="w-[50px] block">
                          {renderProfilePicture()}
                        </Link>

                        <Link
                          to={SITELINK.COMPOSER}
                          className={`flex justify-center items-center gap-2 w-full madimi-one text-sm p-1 px-4 text-start font-[400] border rounded-[50px] ${
                            isDarkMode 
                              ? 'border-gray-600 bg-neutral-700 text-gray-300' 
                              : 'border-[#E7ECF0] bg-[#E7ECF0] text-gray-600'
                          }`}
                        >
                          <span className="text-[16px] tracking-[-1px]">
                            Have something to say?
                          </span>
                          <LuCamera className="text-[24px]" />
                        </Link>
                      </div>

                      {isFeedLoading ? (
                        <SkeletonFeed count={3} variant="post" />
                      ) : content && content.length > 0 ? (
                        <>
                          <div className="text-sm">{content}</div>
                          {isFetchingNextPage && (
                            <div className="flex py-5 justify-center">
                              <img 
                                src={Loader} 
                                alt="loader" 
                                className="h-[20px] w-[20px]"
                                style={{ filter: isDarkMode ? 'invert(1)' : 'none' }}
                              />
                            </div>
                          )}
                          {!hasNextPage && (
                            <div className={`text-xs flex justify-center pt-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              No more content
                            </div>
                          )}
                        </>
                      ) : (
                        <CommunityEmptyState />
                      )}
                    </section>
                  </TabsContent>

                  <TabsContent value="member">
                    <div className="flex flex-col gap-3 max-w-3xl mx-auto w-full px-4 pb-24">
                      {/* Members Header */}
                      <div className={`flex items-center justify-between mb-4 pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2">
                          <LuUsers className={`text-xl ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Community Members
                          </h3>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-neutral-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                          {members?.data?.data?.community_members?.length} members
                        </div>
                      </div>

                      {/* Members Grid */}
                      {members.isSuccess && members?.data?.data?.community_members?.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {members?.data?.data?.community_members?.map(
                            (member: Member, index: number) => {
                              const isCreator = member.name === userCommunityDetails.data?.data?.creator?.username;
                              
                              return (
                                <div
                                  key={index}
                                  className={`rounded-xl p-4 transition-all duration-200 hover:shadow-md ${
                                    isDarkMode 
                                      ? 'bg-neutral-800/80 border border-gray-700 hover:bg-neutral-700' 
                                      : 'bg-white border border-gray-100 shadow-sm hover:shadow-md'
                                  }`}
                                >
                                  <div className="flex items-start gap-3">
                                    {/* Avatar */}
                                    <Link to={`${SITELINK.MY_PROFILE}/${member.username}`} className="shrink-0">
                                      <div className="relative">
                                        <img
                                          src={member.profile_picture || Avatar}
                                          className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm"
                                          alt={member.username}
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).src = Avatar;
                                          }}
                                        />
                                        {isCreator && (
                                          <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full p-0.5">
                                            <LuCrown className="w-3 h-3 text-white" />
                                          </div>
                                        )}
                                      </div>
                                    </Link>

                                    {/* Member Info */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <Link to={`${SITELINK.MY_PROFILE}/${member.username}`}>
                                          <h4 className={`font-semibold text-sm truncate hover:underline ${
                                            isDarkMode ? 'text-white' : 'text-gray-900'
                                          }`}>
                                            {member.username}
                                          </h4>
                                        </Link>
                                        {isCreator && (
                                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            isDarkMode 
                                              ? 'bg-yellow-900/30 text-yellow-400' 
                                              : 'bg-yellow-100 text-yellow-700'
                                          }`}>
                                            Creator
                                          </span>
                                        )}
                                      </div>
                                      
                                      <p className={`text-xs mt-1 truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {isCreator ? 'Community Creator' : 'Community Member'}
                                      </p>

                                      {/* Contact Actions */}
                                      <div className={`flex items-center gap-2 mt-3 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                                        {member.whatsapp_number && (
                                          <button
                                            onClick={() => chatOnWhatsapp(member.whatsapp_number)}
                                            className={`p-1.5 rounded-full transition-colors ${
                                              isDarkMode 
                                                ? 'hover:bg-green-900/30 text-green-400' 
                                                : 'hover:bg-green-50 text-green-600'
                                            }`}
                                            title="Chat on WhatsApp"
                                          >
                                            <FaWhatsapp className="w-4 h-4" />
                                          </button>
                                        )}
                                        
                                        {member.phone_number && (
                                          <button
                                            onClick={() => handleTelegramClick(member.phone_number)}
                                            className={`p-1.5 rounded-full transition-colors ${
                                              isDarkMode 
                                                ? 'hover:bg-blue-900/30 text-blue-400' 
                                                : 'hover:bg-blue-50 text-blue-600'
                                            }`}
                                            title="Message on Telegram"
                                          >
                                            <FaTelegram className="w-4 h-4" />
                                          </button>
                                        )}
                                        
                                        <Link
                                          to={SITELINK.CHATIN}
                                          onClick={() => {
                                            localStorage.setItem(
                                              'lastChattedUserInfo',
                                              JSON.stringify({
                                                id: member?.user_id,
                                                name: member.username,
                                                image: member.profile_picture,
                                              }),
                                            );
                                          }}
                                          className={`p-1.5 rounded-full transition-colors ${
                                            isDarkMode 
                                              ? 'hover:bg-purple-900/30 text-purple-400' 
                                              : 'hover:bg-purple-50 text-purple-600'
                                          }`}
                                          title="Send direct message"
                                        >
                                          <LuMessageCircle className="w-4 h-4" />
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      ) : (
                        <div className={`text-center py-16 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <LuUsers className="w-16 h-16 mx-auto mb-3 opacity-50" />
                          <p className="text-sm">No members found</p>
                          <p className="text-xs mt-1">Be the first to join this community!</p>
                        </div>
                      )}

                      {/* Leave Button */}
                      <div className={`flex justify-center mt-8 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                          disabled={isLoadingBtn}
                          className={`px-8 py-2.5 rounded-full font-medium text-sm transition-all duration-200 min-w-[200px] ${
                            isDarkMode 
                              ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-800' 
                              : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          onClick={leaveCommunity}
                        >
                          {isLoadingBtn ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              Leaving...
                            </span>
                          ) : (
                            'Leave Community'
                          )}
                        </button>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default CommunityDetails;